import { NgClass, NgFor, NgIf, NgStyle } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { BadgeModule } from 'primeng/badge';
import {
  Observable,
  Subject,
  distinctUntilChanged,
  filter,
  map,
  takeUntil,
  tap,
} from 'rxjs';
import { LiveSupportService } from 'src/app/pages/live-support/live-support.service';
import { CollapseLayoutService } from '../../services/collapseLayout.service';
import { HomeService } from '../../services/home.service';
import { InterventionService } from '../../services/intervention.service';
import { TutorialService } from '../../services/tutorial.service';
import { UserInfoService } from '../../services/user-info.service';
import { AppConstant } from '../../utilities/app.constant';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['../../../../assets/styles/share/_sidebar.scss'],
  imports: [NgStyle, NgClass, NgFor, NgIf, BadgeModule, SvgIconComponent],
})
export class SidebarComponent implements OnInit {
  public tabs: any[] = [
    {
      title: 'Home',
      svg: 'home',
      sizeIcon: 'md',
      isActive: false,
      path: 'home',
    },
  ];

  currentUser: any;
  numberUnreadSupportCases: number = 0;
  destroy$ = new Subject<void>();

  menuData: any[] = [
    {
      title: 'Home',
      svg: 'home',
      sizeIcon: 'md',
      isActive: false,
      path: 'home',
      role: [
        AppConstant.ROLES.ADMIN.label,
        AppConstant.ROLES.ENGINEER.label,
        AppConstant.ROLES.MANAGER.label,
        AppConstant.ROLES.VIEWER.label,
        AppConstant.ROLES.SUPPORT.value.data,
      ],
    },
    {
      title: 'Alerts Management',
      svg: 'alert',
      sizeIcon: 'md',
      isActive: false,
      path: 'alerts/alerts-management',
      role: [
        AppConstant.ROLES.ADMIN.label,
        AppConstant.ROLES.ENGINEER.label,
        AppConstant.ROLES.MANAGER.label,
        AppConstant.ROLES.VIEWER.label,
        AppConstant.ROLES.SUPPORT.value.data,
      ],
    },
    {
      title: 'Rig Management',
      svg: 'rig',
      sizeIcon: 'md',
      isActive: false,
      path: 'rig/rig-management',
      role: [
        AppConstant.ROLES.ADMIN.label,
        AppConstant.ROLES.VIEWER.label,
        AppConstant.ROLES.SUPPORT.value.data,
      ],
    },
    {
      title: 'Settings and Team Management',
      svg: 'user',
      sizeIcon: 'md',
      isActive: false,
      path: 'user/team-management',
      role: [
        AppConstant.ROLES.ADMIN.label,
        AppConstant.ROLES.MANAGER.label,
        AppConstant.ROLES.VIEWER.label,
        AppConstant.ROLES.SUPPORT.value.data,
      ],
    },
    {
      title: 'Mailbox Management',
      svg: 'mailbox',
      sizeIcon: 'md',
      isActive: false,
      path: 'mailbox/mailbox-management',
      role: [
        AppConstant.ROLES.ADMIN.label,
        AppConstant.ROLES.VIEWER.label,
        AppConstant.ROLES.SUPPORT.value.data,
      ],
    },
    {
      title: 'Knowledge Center',
      svg: 'helpCenter',
      sizeIcon: 'md',
      isActive: false,
      path: 'help-center',
      role: [
        AppConstant.ROLES.ADMIN.label,
        AppConstant.ROLES.ENGINEER.label,
        AppConstant.ROLES.MANAGER.label,
        AppConstant.ROLES.VIEWER.label,
        AppConstant.ROLES.SUPPORT.value.data,
      ],
    },
  ];

  @Input() expandMenu$: boolean = false;

  constructor(
    private _router: Router,
    private _userInfoService: UserInfoService,
    private _interventionService: InterventionService,
    private _tutorialService: TutorialService,
    private _collapseLayoutService: CollapseLayoutService,
    private _homeService: HomeService,
    private liveSupportService: LiveSupportService
  ) {}

  ngOnInit() {
    this.getUnreadForLiveSuppport();
    this.getCurrentUrl()
      .pipe(takeUntil(this.destroy$))
      .subscribe((url: string) => {
        this._tutorialService.setCurrentPathSubject(url);
        this.activeModuleTracking(url);
      });

    this._userInfoService.userSubject$
      .pipe(
        takeUntil(this.destroy$),
        filter((userResponse: any) => !!userResponse),
        filter(
          (userResponse: any) =>
            JSON.stringify(userResponse) != JSON.stringify(this.currentUser)
        ),
        tap((userResponse: any) => (this.currentUser = userResponse))
      )
      .subscribe((userResponse: any) => {
        const user = userResponse || null;
        this.tabs = [];
        if (user && user.id && user.role) {
          this.tabs = this.menuData.filter(tab => {
            return tab.role.includes(user.role);
          });
          // simulate live support role
          if (
            [
              AppConstant.ROLES.SUPPORT.value.data,
              AppConstant.ROLES.ADMIN.value.data,
            ].includes(user.role)
          ) {
            this.tabs.push({
              title: 'Live Support',
              svg: 'liveSupport',
              sizeIcon: 'lg',
              isActive: false,
              path: 'live-support',
              isSub: true,
              role: [
                AppConstant.ROLES.SUPPORT.value.data,
                AppConstant.ROLES.ADMIN.value.data,
              ],
            });
          }
        }

        this.activeModuleTracking(this._router.url);
        this._tutorialService.setUserTutorialSubject(this.tabs);
      });
  }

  getUnreadForLiveSuppport() {
    this.liveSupportService.numberSupportCasesUnread$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: any) => {
        this.numberUnreadSupportCases = value || 0;
      });
  }

  activeModuleTracking(url: string) {
    this.tabs?.forEach(tab => {
      const result = url.includes(tab.path);
      tab.isActive = result;
    });
  }

  onTab(path: string) {
    const normalizedPath = path.toLowerCase();
    const currentPath = this._router.url.split('?')[0].replace(/^\//, '');

    // Skip no-op navigation to prevent duplicate route cycles and flicker.
    if (currentPath === normalizedPath) {
      return;
    }

    if (path.includes('home')) {
      this._interventionService.resetLogFilter();
      // Set Default Filter -- Without CONSTANTS
      this._homeService.setPayloadFilterDefault();
      this._collapseLayoutService.updateCollapseLayout(true);
    }
    this._router.navigate([normalizedPath]);
  }

  getCurrentUrl(): Observable<string> {
    return this._router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event: NavigationEnd) => {
        const url = event.url === '/' ? 'home' : event.url;
        return url;
      }),
      distinctUntilChanged()
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
